<template>
  <ElAutocomplete
    ref="autocomplete"
    class="license-name-input"
    type="textarea"
    :autosize="autosize"
    :placeholder="placeholder"
    :value="value"
    :fetch-suggestions="fetchSuggestions"
    :debounce="100"
    :trigger-on-focus="false"
    popper-class="license-name-input__popper"
    @input="emit('input', $event)"
    @select="emit('input', $event.value)"
  ></ElAutocomplete>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { Autocomplete as ElAutocomplete } from 'element-ui'
import {
  loadLicenseSuggestIndex,
  searchLicenseSuggestions
} from '@/utils/format-edit/licenseSuggest'

withDefaults(
  defineProps<{
    value?: string
    placeholder?: string
    autosize?: { minRows: number; maxRows: number }
  }>(),
  { value: '', placeholder: '', autosize: () => ({ minRows: 1, maxRows: 4 }) }
)

const emit = defineEmits<{ (e: 'input', value: string): void }>()

type ElAutocompleteLike = {
  $refs: { input?: { resizeTextarea?: () => void } }
}

const autocomplete = ref<ElAutocompleteLike | null>(null)

function fetchSuggestions(
  query: string,
  respond: (items: { value: string }[]) => void
) {
  loadLicenseSuggestIndex()
    .then((index) =>
      respond(
        searchLicenseSuggestions(index, query || '').map((value) => ({
          value
        }))
      )
    )
    .catch(() => respond([]))
}

// 親（HistoryItem）がカード切替時に textarea の高さを合わせ直すため
defineExpose({
  resizeTextarea: () => autocomplete.value?.$refs.input?.resizeTextarea?.()
})
</script>

<style scoped lang="scss">
.license-name-input {
  width: 100%;
}
</style>

<style lang="scss">
/*
 * popper は body 直下に出るため scoped にできない。
 * theme-chalk の autocomplete.scss はアプリで読み込んでいないので、候補リスト部分だけ持つ
 */
$white: #fff;
$border-color: #e4e7ed;
$hover-color: #f5f7fa;
$text-color: #606266;

.license-name-input__popper {
  margin: 5px 0;
  box-shadow: 0 2px 12px 0 rgb(0 0 0 / 10%);
  border-radius: 4px;
  border: 1px solid $border-color;
  box-sizing: border-box;
  background-color: $white;

  .el-autocomplete-suggestion__wrap {
    max-height: 280px;
    padding: 6px 0;
    box-sizing: border-box;
  }

  .el-autocomplete-suggestion__list {
    margin: 0;
    padding: 0;
  }

  li {
    padding: 8px 16px;
    margin: 0;
    line-height: 1.5;
    cursor: pointer;
    color: $text-color;
    font-size: 14px;
    list-style: none;
    white-space: normal;

    &:hover,
    &.highlighted {
      background-color: $hover-color;
    }
  }
}
</style>
